import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, CalendarDays } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, isSameMonth, isSameDay, parseISO } from 'date-fns';

const TYPE_COLORS: Record<string, string> = {
  class: '#1a56db',
  exam: '#e02424',
  announcement: '#7c3aed',
  event: '#057a55',
  meeting: '#057a55',
  holiday: '#d97706',
};

const typeColor = (t: string) => TYPE_COLORS[t] || '#6b7280';

export default function CalendarPage() {
  const [month, setMonth] = useState(new Date());
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { user } = useAuthStore();
  const canManage = ['admin', 'superadmin', 'registrar'].includes(user?.role || '');

  const rangeStart = startOfWeek(startOfMonth(month));
  const rangeEnd = endOfWeek(endOfMonth(month));

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/calendar', { params: { start: rangeStart.toISOString(), end: rangeEnd.toISOString() } });
      setEvents(r.data.data.events || []);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const days = useMemo(() => {
    const out: Date[] = [];
    let d = rangeStart;
    while (d <= rangeEnd) { out.push(d); d = addDays(d, 1); }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const eventsByDay = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const e of events) {
      const key = format(parseISO(e.start), 'yyyy-MM-dd');
      if (!map[key]) map[key] = [];
      map[key].push(e);
    }
    return map;
  }, [events]);

  const selectedEvents = selectedDay ? (eventsByDay[format(selectedDay, 'yyyy-MM-dd')] || []) : [];

  const deleteEvent = async (id: string) => {
    if (!id.startsWith('event-')) return;
    if (!window.confirm('Delete this event?')) return;
    try { await api.delete(`/calendar/events/${id.replace('event-', '')}`); toast.success('Event deleted'); fetchEvents(); }
    catch { toast.error('Failed to delete event'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><h1 className="page-title">Calendar</h1><p className="page-subtitle">Classes, exams, announcements and events in one place</p></div>
        {canManage && <button className="btn btn-primary" onClick={() => setShowModal(true)}><Plus size={16} />Add Event</button>}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setMonth(m => addMonths(m, -1))}><ChevronLeft size={16} /></button>
          <h3 style={{ fontWeight: 700, fontSize: 17 }}>{format(month, 'MMMM yyyy')}</h3>
          <button className="btn btn-secondary btn-sm" onClick={() => setMonth(m => addMonths(m, 1))}><ChevronRight size={16} /></button>
        </div>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14, fontSize: 12 }}>
          {Object.entries(TYPE_COLORS).filter(([k]) => ['class','exam','announcement','event','holiday'].includes(k)).map(([type, color]) => (
            <span key={type} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: 'inline-block' }} />
              <span style={{ textTransform: 'capitalize', color: 'var(--text-muted)' }}>{type}</span>
            </span>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', padding: '4px 0' }}>{d}</div>
            ))}
            {days.map(day => {
              const key = format(day, 'yyyy-MM-dd');
              const dayEvents = eventsByDay[key] || [];
              const inMonth = isSameMonth(day, month);
              const isToday = isSameDay(day, new Date());
              const isSelected = selectedDay && isSameDay(day, selectedDay);
              return (
                <div
                  key={key}
                  onClick={() => setSelectedDay(day)}
                  style={{
                    minHeight: 86, borderRadius: 8, padding: 6, cursor: 'pointer',
                    background: isSelected ? 'var(--primary-light)' : 'var(--bg)',
                    border: isToday ? '2px solid var(--primary)' : '1px solid var(--border)',
                    opacity: inMonth ? 1 : 0.45,
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{format(day, 'd')}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {dayEvents.slice(0, 3).map(e => (
                      <div key={e.id} title={e.title} style={{
                        fontSize: 10, padding: '2px 5px', borderRadius: 4, color: '#fff',
                        background: typeColor(e.type), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{e.title}</div>
                    ))}
                    {dayEvents.length > 3 && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{dayEvents.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedDay && (
        <div className="card">
          <h3 style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>{format(selectedDay, 'EEEE, d MMMM yyyy')}</h3>
          {selectedEvents.length === 0 ? (
            <div className="empty-state"><CalendarDays size={40} /><p>No events on this day.</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {selectedEvents.map(e => (
                <div key={e.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: 12, borderRadius: 8, background: 'var(--bg)', borderLeft: `4px solid ${typeColor(e.type)}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{e.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {format(parseISO(e.start), 'h:mm a')}{e.end && e.end !== e.start ? ` – ${format(parseISO(e.end), 'h:mm a')}` : ''}
                      {e.meta?.venue && ` · ${e.meta.venue}`}
                      {e.meta?.room && ` · Room ${e.meta.room}`}
                    </div>
                    {(e.meta?.description || e.meta?.content) && <p style={{ fontSize: 13, marginTop: 6, color: 'var(--text-muted)' }}>{e.meta.description || e.meta.content}</p>}
                    <span className="badge badge-info" style={{ marginTop: 6, textTransform: 'capitalize' }}>{e.type}</span>
                  </div>
                  {canManage && e.source === 'event' && (
                    <button onClick={() => deleteEvent(e.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 18 }}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showModal && <EventModal defaultDate={selectedDay} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); fetchEvents(); }} />}
    </div>
  );
}

function EventModal({ defaultDate, onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({
    event_type: 'event',
    target_audience: 'all',
    start_at: defaultDate ? format(defaultDate, "yyyy-MM-dd'T'09:00") : '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.start_at) { toast.error('Title and start date/time are required'); return; }
    setLoading(true);
    try {
      await api.post('/calendar/events', form);
      toast.success('Event created');
      onSaved();
    } catch { toast.error('Failed to create event'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Add Calendar Event</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group"><label className="form-label">Title *</label><input className="form-input" required value={form.title || ''} onChange={e => set('title', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Description</label><textarea className="form-input" rows={3} value={form.description || ''} onChange={e => set('description', e.target.value)} /></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Type</label>
                <select className="form-input form-select" value={form.event_type} onChange={e => set('event_type', e.target.value)}>
                  <option value="event">Event</option>
                  <option value="meeting">Meeting</option>
                  <option value="holiday">Holiday</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Audience</label>
                <select className="form-input form-select" value={form.target_audience} onChange={e => set('target_audience', e.target.value)}>
                  <option value="all">Everyone</option>
                  <option value="students">Students</option>
                  <option value="staff">Staff</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Starts *</label><input type="datetime-local" className="form-input" required value={form.start_at} onChange={e => set('start_at', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Ends</label><input type="datetime-local" className="form-input" value={form.end_at || ''} onChange={e => set('end_at', e.target.value)} /></div>
            </div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Create Event'}</button></div>
        </form>
      </div>
    </div>
  );
}
